'use client';

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Icons (matching Replit's lucide-react icons)
const Shield = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const CheckCircle = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const Key = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m21 2-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4"/>
  </svg>
);

const FileText = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
    <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/>
  </svg>
);

const Globe = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const Award = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/>
  </svg>
);

const Search = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const Copy = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const Download = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const University = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5"/>
  </svg>
);

const Users = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const Clock = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

const Hash = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/>
    <line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/>
  </svg>
);

const AlertCircle = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const ExternalLink = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const ArrowLeft = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);

export default function CredentialVerification() {
  const { hash: urlHash } = useParams();
  const [verificationToken, setVerificationToken] = useState(urlHash || '');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Auto-verify if hash is in URL
  useEffect(() => {
    if (urlHash && urlHash.length > 10) {
      handleVerifyCredential(urlHash);
    }
  }, [urlHash]);

  const handleVerifyCredential = async (tokenToVerify) => {
    const token = tokenToVerify || verificationToken;
    if (!token.trim()) return;
    
    setIsVerifying(true);
    setVerificationResult(null);
    
    try {
      const response = await fetch(`/api/verify/${token}`);
      const data = await response.json();
      setVerificationResult(data);
    } catch (error) {
      setVerificationResult({
        valid: false,
        error: 'Failed to verify credential. Please try again.'
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const useDemoToken = () => {
    // This would be a real hash from the database
    setVerificationToken('demo_verification_token');
  };

  const copyToken = () => {
    navigator.clipboard.writeText(verificationToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleVerifyCredential();
  };

  return (
    <div className="credential-verification-page">
      {/* Navigation */}
      <nav className="cv-nav">
        <div className="cv-nav-content">
          <Link to="/" className="cv-logo">
            <div className="logo-orb" />
            <span>NuMuN</span>
          </Link>
          <Link to="/dashboard" className="cv-back-btn">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </nav>
      
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="cv-hero"
      >
        <h1 className="cv-title">
          Credential <span className="gradient-text">Verification</span>
        </h1>
        <p className="cv-subtitle">
          Multi-layered verification system that ensures every credential is authentic and trustworthy. 
          Verify any NuMuN credential instantly with just a verification token.
        </p>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="cv-features"
        >
          <div className="cv-feature">
            <CheckCircle className="w-4 h-4 text-accent-green" />
            <span>Multi-layer cryptographic proof</span>
          </div>
          <div className="cv-feature">
            <CheckCircle className="w-4 h-4 text-accent-green" />
            <span>Universal public verification</span>
          </div>
          <div className="cv-feature">
            <CheckCircle className="w-4 h-4 text-accent-green" />
            <span>Institutional validation</span>
          </div>
          <div className="cv-feature">
            <CheckCircle className="w-4 h-4 text-accent-green" />
            <span>Standardized rubrics</span>
          </div>
        </motion.div>
      </motion.div>

      {/* Main Content */}
      <div className="cv-main">
        <div className="cv-container">
          
          {/* Public Verification Tool */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="cv-card cv-verification-tool"
          >
            <div className="cv-card-header">
              <Search className="w-6 h-6 text-accent-purple" />
              <h2>Public Verification Tool</h2>
              <span className="cv-badge">No Account Required</span>
            </div>
            
            <p className="cv-card-desc">
              Enter a verification token to instantly validate any NuMuN credential. 
              This tool uses cryptographic verification and blockchain validation.
            </p>
            
            {/* Demo Token Section */}
            <div className="cv-demo-section">
              <div className="cv-demo-icon">
                <Key className="w-5 h-5 text-accent-purple" />
              </div>
              <div className="cv-demo-content">
                <h3>Try Demo Verification</h3>
                <p>
                  Use our sample credential token to see how verification works with real data.
                </p>
                <div className="cv-demo-buttons">
                  <button onClick={useDemoToken} className="cv-btn-demo">
                    <FileText className="w-4 h-4" />
                    Use Demo Token
                  </button>
                  <button onClick={copyToken} className="cv-btn-copy" disabled={!verificationToken}>
                    <Copy className="w-4 h-4" />
                    {copied ? 'Copied!' : 'Copy Token'}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Verification Input */}
            <form onSubmit={handleSubmit} className="cv-input-group">
              <input
                type="text"
                value={verificationToken}
                onChange={(e) => setVerificationToken(e.target.value)}
                placeholder="Enter verification token (e.g., numun_cred_2025_...)"
                className="cv-input"
              />
              <button
                type="submit"
                disabled={!verificationToken.trim() || isVerifying}
                className="cv-btn-verify"
              >
                {isVerifying ? (
                  <>
                    <div className="cv-spinner" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    Verify
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Verification Result */}
          {verificationResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="cv-result-container"
            >
              {verificationResult.valid ? (
                <div className="cv-result cv-result-success">
                  <div className="cv-result-header">
                    <div className="cv-result-icon success">
                      <CheckCircle className="w-6 h-6" />
                    </div>
                    <div>
                      <h3>Credential Verified</h3>
                      <p>This credential is authentic and valid</p>
                    </div>
                  </div>
                  
                  <div className="cv-result-grid">
                    {/* Hours Invested - PRIMARY METRIC */}
                    <div className="cv-hours-hero">
                      <div className="cv-hours-total">
                        <span className="cv-hours-number">
                          {verificationResult.record?.hoursInvested?.total || 0}
                        </span>
                        <span className="cv-hours-label">hours invested</span>
                      </div>
                      <div className="cv-hours-breakdown">
                        <div className="cv-hours-item">
                          <span className="cv-hours-value">
                            {verificationResult.record?.hoursInvested?.canvasWork || 0}
                          </span>
                          <span className="cv-hours-sublabel">Canvas Work</span>
                        </div>
                        <span className="cv-hours-plus">+</span>
                        <div className="cv-hours-item">
                          <span className="cv-hours-value">
                            {verificationResult.record?.hoursInvested?.outsideLearning || 0}
                          </span>
                          <span className="cv-hours-sublabel">Outside Learning</span>
                        </div>
                      </div>
                      
                      {/* Hours by type */}
                      {verificationResult.record?.hoursInvested?.breakdown?.length > 0 && (
                        <div className="cv-hours-types">
                          {verificationResult.record.hoursInvested.breakdown.map((b, i) => (
                            <span key={i} className="cv-hours-type">
                              {b.type}: <strong>{b.hours} hrs</strong>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Credential Details */}
                    <div className="cv-details-section">
                      <h4>
                        <Award className="w-5 h-5 text-accent-purple" />
                        Credential Details
                      </h4>
                      <div className="cv-details-list">
                        <div className="cv-detail">
                          <span className="cv-detail-label">Learner:</span>
                          <span className="cv-detail-value">{verificationResult.learner?.name}</span>
                        </div>
                        <div className="cv-detail">
                          <span className="cv-detail-label">Course:</span>
                          <span className="cv-detail-value">{verificationResult.record?.courseName}</span>
                        </div>
                        <div className="cv-detail">
                          <span className="cv-detail-label">Institution:</span>
                          <span className="cv-detail-value">{verificationResult.record?.institution}</span>
                        </div>
                        <div className="cv-detail">
                          <span className="cv-detail-label">Instructor:</span>
                          <span className="cv-detail-value">{verificationResult.record?.instructor || 'N/A'}</span>
                        </div>
                        <div className="cv-detail">
                          <span className="cv-detail-label">Term:</span>
                          <span className="cv-detail-value">{verificationResult.record?.term}</span>
                        </div>
                        {verificationResult.record?.grade && (
                          <div className="cv-detail">
                            <span className="cv-detail-label">Grade:</span>
                            <span className="cv-detail-value cv-grade">
                              {verificationResult.record.grade}
                              {verificationResult.record.gradePercent && (
                                <span className="cv-grade-percent">({verificationResult.record.gradePercent}%)</span>
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Competencies */}
                  {verificationResult.record?.competencies?.length > 0 && (
                    <div className="cv-competencies-section">
                      <h4>
                        <Users className="w-5 h-5 text-accent-green" />
                        Competencies Developed
                      </h4>
                      <div className="cv-competencies-grid">
                        {verificationResult.record.competencies.map((comp, idx) => (
                          <div key={idx} className="cv-competency-card">
                            <div className="cv-comp-header">
                              <span className="cv-comp-name">{comp.name}</span>
                              {comp.hoursApplied && (
                                <span className="cv-comp-hours">{comp.hoursApplied} hrs</span>
                              )}
                            </div>
                            {comp.proficiency && (
                              <div className="cv-comp-bar">
                                <div 
                                  className="cv-comp-fill" 
                                  style={{ width: `${comp.proficiency}%` }}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Cryptographic Details */}
                  <div className="cv-crypto-section">
                    <h4>
                      <Hash className="w-5 h-5 text-accent-cyan" />
                      Cryptographic Verification
                    </h4>
                    <div className="cv-crypto-grid">
                      <div className="cv-crypto-item">
                        <span className="cv-crypto-label">Hash:</span>
                        <code className="cv-crypto-value">{verificationResult.hash}</code>
                      </div>
                      <div className="cv-crypto-item">
                        <span className="cv-crypto-label">Verified At:</span>
                        <span className="cv-crypto-value">
                          {new Date(verificationResult.verifiedAt).toLocaleString()}
                        </span>
                      </div>
                      <div className="cv-crypto-item">
                        <span className="cv-crypto-label">Status:</span>
                        <span className="cv-crypto-status">Verified ✓</span>
                      </div>
                      {verificationResult.verification?.hashCount > 1 && (
                        <div className="cv-crypto-item">
                          <span className="cv-crypto-label">Updates:</span>
                          <span className="cv-crypto-value">
                            {verificationResult.verification.hashCount} revisions
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="cv-actions">
                    <button className="cv-btn-secondary">
                      <Download className="w-4 h-4" />
                      Download Certificate
                    </button>
                    <button className="cv-btn-secondary">
                      <ExternalLink className="w-4 h-4" />
                      View Full Record
                    </button>
                  </div>
                </div>
              ) : (
                <div className="cv-result cv-result-error">
                  <div className="cv-result-header">
                    <div className="cv-result-icon error">
                      <AlertCircle className="w-6 h-6" />
                    </div>
                    <div>
                      <h3>Verification Failed</h3>
                      <p>This credential could not be verified</p>
                    </div>
                  </div>
                  <p className="cv-error-message">
                    {verificationResult.error || verificationResult.message || 'The verification token is invalid or expired.'}
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Features Grid */}
          <div className="cv-features-grid">
            {[
              {
                icon: Shield,
                title: "Cryptographic Security",
                description: "SHA-256 hashing and digital signatures ensure credential integrity"
              },
              {
                icon: Globe,
                title: "Public Verification",
                description: "Anyone can verify credentials without accounts or special access"
              },
              {
                icon: University,
                title: "Institutional Backing",
                description: "Verified by accredited institutions and recognized supervisors"
              },
              {
                icon: Clock,
                title: "Immutable Records",
                description: "Blockchain timestamps create permanent, tamper-proof verification"
              }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + idx * 0.1 }}
                className="cv-feature-card"
              >
                <div className="cv-feature-icon">
                  <feature.icon className="w-8 h-8" />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>

          {/* How It Works */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            className="cv-card cv-how-it-works"
          >
            <h2>How Verification Works</h2>
            <div className="cv-steps">
              <div className="cv-step">
                <div className="cv-step-icon purple">
                  <Hash className="w-8 h-8" />
                </div>
                <h3>Cryptographic Proof</h3>
                <p>Each credential contains a unique cryptographic signature that cannot be forged or replicated</p>
              </div>
              <div className="cv-step">
                <div className="cv-step-icon green">
                  <CheckCircle className="w-8 h-8" />
                </div>
                <h3>Chain Validation</h3>
                <p>Skills are linked in an immutable chain, ensuring the complete learning journey is authentic</p>
              </div>
              <div className="cv-step">
                <div className="cv-step-icon cyan">
                  <Globe className="w-8 h-8" />
                </div>
                <h3>Public Access</h3>
                <p>Anyone can verify credentials instantly without accounts, logins, or special permissions</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
